/**
 * TBB Mobile Menu Bridge
 * ----------------------------------------------------------------------
 * Brings the Qikify mega menu (`.tmenu_app`) into the custom hamburger
 * drawer (`.main-navigation-mobile-drawer`) on mobile, so the merchant
 * maintains a single menu in Qikify and both desktop + mobile pull from
 * that one source of truth.
 *
 * Strategy:
 *   1. Wait for both Qikify's menu DOM AND the drawer mount point to exist
 *   2. Clone Qikify's mega submenu (just the data — items, headings,
 *      product cards) into the drawer's `[data-tbb-qikify-mount]` div
 *   3. Restyling into mobile accordion form is handled by CSS — this
 *      script only handles DOM placement
 *
 * Lifecycle:
 *   - Runs once per drawer open (idempotent — won't re-clone if already
 *     populated)
 *   - Re-clones if Qikify re-renders its menu (rare — caught by a small
 *     MutationObserver on the source)
 */
(function () {
  'use strict';

  // Track which drawer mount points have already been populated, so
  // re-opens of the same drawer don't keep cloning.
  var POPULATED = new WeakSet();

  /**
   * Find Qikify's mega submenu source (the dropdown panel that contains
   * the column structure). We don't clone the trigger LI — only the UL
   * with the actual menu data.
   */
  function findQikifySource() {
    return document.querySelector(
      '.tmenu_app .tmenu_item--root > .tmenu_submenu_type_mega'
    );
  }

  /**
   * Hide the drawer's native "Throwback Collections" lvl0 item so it
   * doesn't duplicate with the Qikify-driven version we're injecting.
   * Search by text match within the same drawer the mount lives in.
   */
  function hideNativeDuplicate(mount) {
    var drawer = mount.closest('.main-navigation-mobile-drawer, [class*="main-navigation-mobile"]')
      || mount.closest('main-navigation-mobile')
      || document;
    var navItems = drawer.querySelectorAll('.main-navigation-mobile-nav-item');
    navItems.forEach(function (item) {
      var link = item.querySelector('.main-navigation-mobile-nav-link');
      if (!link) return;
      var text = (link.textContent || '').trim().toLowerCase();
      if (text === 'throwback collections' || text.indexOf('throwback collections') !== -1) {
        item.setAttribute('data-tbb-hidden-by-qikify', '1');
        item.style.display = 'none';
      }
    });
  }

  /**
   * Inject Qikify's menu into a drawer mount point. Returns true on
   * success, false if either side wasn't ready.
   */
  function injectInto(mount) {
    if (!mount || POPULATED.has(mount)) return true;

    var source = findQikifySource();
    if (!source) return false;

    var clone = source.cloneNode(true);
    clone.classList.add('tbb-qikify-cloned');

    // Wrap in a collapsible parent — gives "Throwback Collections" its
    // own tap-to-expand row in the drawer, drill-down style.
    var details = document.createElement('details');
    details.className = 'tbb-qikify-collapsible';
    details.setAttribute('open', ''); // open by default so user sees content
    var summary = document.createElement('summary');
    summary.className = 'tbb-qikify-collapsible-summary';
    summary.textContent = 'Throwback Collections';
    details.appendChild(summary);
    details.appendChild(clone);

    mount.innerHTML = '';
    mount.appendChild(details);
    POPULATED.add(mount);

    // Hide the native lvl0 "Throwback Collections" item to prevent
    // duplicate menus in the drawer.
    hideNativeDuplicate(mount);
    return true;
  }

  /**
   * Find every mount point currently in the live DOM and try to
   * populate it. Call repeatedly while drawers are still being created.
   */
  function tryAllMounts() {
    var mounts = document.querySelectorAll('[data-tbb-qikify-mount]');
    var allDone = true;
    mounts.forEach(function (m) {
      if (!POPULATED.has(m)) {
        if (!injectInto(m)) allDone = false;
      }
    });
    return allDone;
  }

  /**
   * Watch for new drawer instances appearing in the DOM (modal-trigger
   * clones the template the first time it opens). Once a drawer is
   * inserted, run injection.
   */
  function watchForDrawers() {
    var observer = new MutationObserver(function () {
      tryAllMounts();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Watch Qikify's source menu for late hydration / re-render. If the
   * source updates, invalidate any populated mounts so the next open
   * gets fresh content.
   */
  function watchQikifySource() {
    var source = findQikifySource();
    if (!source) return;
    var sourceObserver = new MutationObserver(function () {
      // Wipe POPULATED so re-clones can happen. We can't clear a
      // WeakSet directly, so swap it out for a new one.
      POPULATED = new WeakSet();
      tryAllMounts();
    });
    sourceObserver.observe(source, {
      childList: true,
      subtree: true,
      attributes: false
    });
  }

  function init() {
    // First-pass injection (in case both sides are already in the DOM).
    tryAllMounts();
    // Watch for late drawer creation.
    watchForDrawers();
    // Watch Qikify source for content changes.
    watchQikifySource();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
